import type { FieldPath, FieldValues, UseControllerProps } from "react-hook-form";
import { useController } from "react-hook-form";

import type { SearchableSelectProps } from "@/design-system/SearchableSelect";
import { SearchableSelect } from "@/design-system/SearchableSelect";
import { isDefined } from "@/helpers/util";

import { FormErrorWrapper } from "./FormErrorWrapper";

type DistributiveOmit<T, TKey extends PropertyKey> = T extends unknown ? Omit<T, TKey> : never;

type FormSearchableSelectProps<
  TFormValues extends FieldValues,
  T,
  TEmptyItem extends React.ReactNode | undefined = undefined,
> = DistributiveOmit<SearchableSelectProps<T, TEmptyItem>, "selected" | "onChange"> &
  UseControllerProps<TFormValues, FieldPath<TFormValues>>;

export function FormSearchableSelect<
  TFormValues extends FieldValues,
  T,
  TEmptyItem extends React.ReactNode | undefined = undefined,
>({
  name,
  control,
  rules,
  defaultValue,
  ...props
}: FormSearchableSelectProps<TFormValues, T, TEmptyItem>): React.ReactNode {
  const {
    field,
    fieldState: { error },
  } = useController<TFormValues>({ name, control, rules, defaultValue });

  return (
    <FormErrorWrapper<TFormValues> name={name}>
      <SearchableSelect
        {...props}
        selected={field.value}
        onChange={(value) => field.onChange(value === undefined && props.isClearable ? null : value)}
        aria-invalid={isDefined(error)}
      />
    </FormErrorWrapper>
  );
}
